import { Download, PhoneOff, Search } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, DescriptionItem } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState, ErrorState, Spinner, TableSkeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MonoCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { Page, PageHeader, Toolbar } from "@/components/layout/page";
import {
  CallPager, CallRangeLabel, MyDirectionIcon, MediaCell, MediaHead, SummaryTile,
} from "@/features/call-report";
import { CallAudio } from "@/features/call-audio";
import { ApiError, fetchFile, qs, saveBlob } from "@/lib/api";
import { CallTypeChip } from "@/features/call-type";
import { useAuth } from "@/lib/auth";
import { CallMarkBadge } from "@/features/call-mark";
import { CallQualityCell, CallQualityDetail } from "@/features/call-quality";
import { callOutcome, endedBy, talkShown } from "@/lib/call-outcome";
import {
  DEFAULT_RANGE, RANGES, counterparty, formatSpan, myDirection, placedBy, rangeSince, type Range,
} from "@/lib/calls";
import {
  CALLS_PAGE_SIZE, useCall, useCallSummary, useCalls, type CallFilters,
} from "@/lib/queries";
import { CALL_LEG_LABELS, OWN_NON_CALL_LEGS, atLeast, broughtInBy, type Cdr } from "@/lib/types";
import { formatDateTime, formatDuration } from "@/lib/utils";
import { DialNumberButton } from "@/softphone/dial-number";
import { useDebounced } from "@/lib/use-debounced";

/**
 * Call history — the calls the signed-in user's own phone was on.
 *
 * Separate from `routes/calls.tsx`, which is the floor's report, for three reasons that are
 * worth stating because "add a filter to the report" was the obvious alternative:
 *
 * 1. **It has to be reachable by a rep**, and the report is not. A rep's own work is theirs;
 *    a searchable log of everyone else's conversations is not. Two screens means a rep is never
 *    one cleared filter away from the second — the filter would be the only thing standing
 *    between them, and filters are made to be cleared.
 * 2. **The questions differ.** "How did my day go" wants who, when, how long and how it ended.
 *    "What is the floor doing" wants campaigns, legs, realms and a hangup-cause breakdown. The
 *    second set is noise on a personal history and the first is too thin for a supervisor.
 * 3. **It is the same rows either way.** `mine=true` is the same narrowing core-api applies to
 *    a rep unasked, so this screen shows a supervisor their own calls rather than a subset of
 *    the floor's — the tab means the same thing whoever opens it.
 *
 * A row is one **leg** — and specifically one of *mine*. The switch writes a leg per phone, so
 * an internal call between two reps is two rows carrying identical numbers, one owned by each
 * party; core-api matches the endpoint the channel belonged to (`presence_id`, stored as
 * `cdrs.endpoint_user`) rather than "my number is on the row", which matched both halves and
 * listed every internal call twice here. The colleague's leg is in the colleague's history.
 */

export function HistoryPage() {
  const { user } = useAuth();
  const toast = useToast();

  const [search, setSearch] = React.useState("");
  const [range, setRange] = React.useState<Range>(DEFAULT_RANGE);
  // Placed or received — the reader's own split, not the switch's `direction` column. See
  // `myDirection`; the server narrows on the same rule.
  const [direction, setDirection] = React.useState("all");
  const [answered, setAnswered] = React.useState("all");
  const [page, setPage] = React.useState(0);
  const [openId, setOpenId] = React.useState<number | undefined>();
  const [exporting, setExporting] = React.useState(false);
  const debounced = useDebounced(search, 250);

  // The numbers this person's phone answers to. The server matched the legs on exactly this
  // set, so subtracting it from a leg names the other party without a guess.
  const mine = user?.sip_endpoint_numbers ?? [];
  const hasPhone = mine.length > 0;

  // Pinned when the filters change rather than read per render: recomputing "24 hours ago" on
  // every render makes a new query key every time, and the table refetches forever.
  const since = React.useMemo(() => rangeSince(range), [range]);

  const filters: CallFilters = React.useMemo(
    () => ({
      // Sent by every role, including a rep — who is narrowed to the same rows regardless. It
      // is what makes this screen mean "mine" for a supervisor too.
      mine: true,
      // A rep's own line into their own conference room is not a conversation with anybody —
      // and on a working floor those legs outnumber the calls, so leaving them in would bury
      // the history in rows whose other party is the reader. The header says they are out. A
      // sound test is the same kind of row: the rep and the switch, nobody else.
      exclude_leg: OWN_NON_CALL_LEGS,
      q: debounced || undefined,
      since,
      mine_direction: direction === "all" ? undefined : direction,
      answered: answered === "all" ? undefined : answered === "yes",
    }),
    [debounced, since, direction, answered],
  );

  React.useEffect(() => setPage(0), [filters]);

  // No phone means no calls, and the server agrees — but asking anyway would spend two requests
  // to be told so on every visit.
  const list = useCalls(filters, page, { enabled: hasPhone });
  const summary = useCallSummary(filters, { enabled: hasPhone });

  const filtered =
    Boolean(debounced) || direction !== "all" || answered !== "all" || range !== DEFAULT_RANGE;

  function clearAll() {
    setSearch("");
    setRange(DEFAULT_RANGE);
    setDirection("all");
    setAnswered("all");
  }

  async function exportCsv() {
    setExporting(true);
    try {
      const { blob, headers } = await fetchFile(`/cdrs/export.csv${qs(filters)}`);
      const stamp = new Date().toISOString().slice(0, 10);
      saveBlob(blob, `my-calls-${stamp}.csv`);
      if (headers.get("X-Export-Truncated") === "true") {
        toast.info(
          "Export truncated",
          "The file hit the row cap. Narrow the date range to export the rest.",
        );
      } else {
        toast.success("Export downloaded");
      }
    } catch (error) {
      toast.error(
        "Could not export",
        error instanceof ApiError ? error.message : "The download did not start.",
      );
    } finally {
      setExporting(false);
    }
  }

  const rows = list.data?.items ?? [];
  const total = list.data?.total ?? null;
  const from = page * CALLS_PAGE_SIZE;

  if (!hasPhone) {
    return (
      <Page>
        <PageHeader title="Call history" description="The calls your phone has been on." />
        <Card>
          {/* Not "no calls yet": the two look identical on screen and mean completely different
              things, and only one of them is fixed by waiting. */}
          <EmptyState
            icon={PhoneOff}
            title="No phone is assigned to your account"
            description={
              "A call is recorded against the endpoint that was on it, so there is nothing to " +
              "show until an admin assigns you one. Ask them to link an endpoint to your account."
            }
          />
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        title="Call history"
        description={
          `Calls your phone (${user?.sip_endpoint_extension}) was on — one row per call, the leg ` +
          `your own phone was on. Opening your own room and sound tests are not listed.`
        }
        action={
          <Button variant="outline" onClick={() => void exportCsv()} disabled={exporting}>
            {exporting ? <Spinner /> : <Download />}
            Export CSV
          </Button>
        }
      />

      <div className="mb-4 grid grid-cols-3 gap-3">
        <SummaryTile
          label="Calls"
          value={summary.data ? summary.data.calls.toLocaleString() : "—"}
          // Placed and received, not the summary's `inbound`/`outbound`: those are the switch's
          // reading of the channel, and a call the console placed by ringing the rep first
          // counts as `outbound` on it. This is the same split the filter below narrows on.
          hint={
            summary.data
              ? `${summary.data.placed_by_me} placed · ${summary.data.received_by_me} received`
              : undefined
          }
          loading={summary.isPending}
        />
        <SummaryTile
          label="Answered"
          // A rate of null is "nothing was dialled", which is not 0%.
          value={
            summary.data?.answer_rate == null
              ? "—"
              : `${Math.round(summary.data.answer_rate * 100)}%`
          }
          hint={
            summary.data
              ? `${summary.data.answered.toLocaleString()} of ${summary.data.calls.toLocaleString()}`
              : undefined
          }
          loading={summary.isPending}
        />
        <SummaryTile
          label="Talk time"
          value={summary.data ? formatSpan(summary.data.talk_seconds) : "—"}
          hint={
            summary.data?.avg_talk_seconds != null
              ? `${formatSpan(summary.data.avg_talk_seconds)} average`
              : undefined
          }
          loading={summary.isPending}
        />
      </div>

      <Card className="overflow-hidden">
        <Toolbar>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search a number or name"
            leading={<Search />}
            className="w-64"
            aria-label="Search my calls"
          />
          <Select value={range} onValueChange={(v) => setRange(v as Range)}>
            <SelectTrigger className="w-40" aria-label="Time range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(RANGES).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={direction} onValueChange={setDirection}>
            <SelectTrigger className="w-40" aria-label="Filter by who called">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* "Placed / Received", not "Outbound / Inbound": on this screen the reader is one
                  end of every call, so the useful question is which end they were. */}
              <SelectItem value="all">Placed or received</SelectItem>
              <SelectItem value="placed">Placed</SelectItem>
              <SelectItem value="received">Received</SelectItem>
            </SelectContent>
          </Select>
          <Select value={answered} onValueChange={setAnswered}>
            <SelectTrigger className="w-36" aria-label="Filter by answered">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Answered or not</SelectItem>
              <SelectItem value="yes">Answered</SelectItem>
              <SelectItem value="no">Unanswered</SelectItem>
            </SelectContent>
          </Select>
          {filtered && (
            <Button variant="ghost" size="sm" onClick={clearAll}>
              Clear
            </Button>
          )}
          <span className="ml-auto flex items-center gap-2 text-xs text-text-muted">
            {list.isFetching && !list.isPending && <Spinner className="size-3" />}
            <CallRangeLabel from={from} count={rows.length} total={total} />
          </span>
        </Toolbar>

        {list.isPending ? (
          <TableSkeleton cols={7} rows={8} />
        ) : list.error ? (
          <ErrorState
            error={list.error}
            title="Could not load your calls"
            action={
              <Button variant="outline" onClick={() => void list.refetch()}>
                Try again
              </Button>
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={PhoneOff}
            title={filtered ? "No calls match those filters" : "No calls yet"}
            description={
              filtered
                ? "Try a wider time range, or clear the filters."
                : "Calls appear here as soon as the switch finishes one on your phone."
            }
            action={
              filtered ? (
                <Button variant="outline" onClick={clearAll}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>With</TableHead>
                  <TableHead>Call type</TableHead>
                  <TableHead className="text-right">Talk</TableHead>
                  <TableHead className="w-[5.5rem] text-center">Audio</TableHead>
                  <TableHead>Outcome</TableHead>
                  <MediaHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((call) => (
                  <HistoryRow
                    key={call.id}
                    call={call}
                    mine={mine}
                    onOpen={() => setOpenId(call.id)}
                  />
                ))}
              </TableBody>
            </Table>
            <CallPager
              page={page}
              count={rows.length}
              total={total}
              onChange={setPage}
              busy={list.isFetching}
            />
          </>
        )}
      </Card>

      <MyCallDialog id={openId} mine={mine} onClose={() => setOpenId(undefined)} />
    </Page>
  );
}

function HistoryRow({
  call, mine, onOpen,
}: {
  call: Cdr;
  mine: readonly string[];
  onOpen: () => void;
}) {
  const other = counterparty(call, mine);
  const placed = myDirection(call, mine);
  const outcome = callOutcome(call);
  return (
    <TableRow
      interactive
      tabIndex={0}
      role="button"
      aria-label={
        `${placed === "received" ? "Call from" : "Call to"} ${other.number ?? "unknown"} ` +
        `at ${formatDateTime(call.start_time)}`
      }
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <TableCell className="whitespace-nowrap text-xs text-text-muted">
        <span className="inline-flex items-center gap-1.5">
          <MyDirectionIcon direction={placed} />
          {formatDateTime(call.start_time)}
        </span>
      </TableCell>
      {/* One party column, not From/To: on half a rep's day the caller is the rep's own
          extension, and a column of your own number tells you nothing. */}
      {other.number ? (
        <MonoCell className="whitespace-nowrap">
          {/* The number is the control, not a Call button beside it: it is what the rep is
              reading, and the commonest thing to want from a call you just had is the same call
              again. Clicking it asks first — see `DialNumberButton`, which is also what keeps a
              second control inside a row that is itself a button honest. */}
          <DialNumberButton number={other.number} name={other.name} className="text-text" />
          {other.name && <span className="ml-1.5 font-sans text-xs text-text-muted">{other.name}</span>}
        </MonoCell>
      ) : (
        <TableCell className="text-text-subtle">—</TableCell>
      )}
      {/* How the call came about, not the campaign: a rep has no use for the campaign here, and
          a supervisor reads it on the Calls report (ACL-649, platform ADR-212). */}
      <TableCell>
        <CallTypeCell type={call.call_type} />
      </TableCell>
      <TableCell className="text-right font-mono text-xs">
        {/* Talk time, not duration: a leg that rang for 40 seconds and was never picked up did
            not occupy anybody, and nor did a wait in a queue that no rep answered. A rep's own
            test call to a campaign's number is a caller like any other. */}
        {talkShown(call) ? formatDuration(call.billsec_seconds) : <span className="text-text-subtle">—</span>}
      </TableCell>
      {/* A rep asks "was that my line or theirs" more often than anyone; the mark answers it
          without them opening the leg. The row's own click must not fire from inside the card. */}
      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
        <CallQualityCell call={call} />
      </TableCell>
      <TableCell>
        <span className="inline-flex items-center gap-1.5">
          <Badge tone={outcome.tone} size="sm">
            {outcome.label}
          </Badge>
          <CallMarkBadge call={call} />
        </span>
      </TableCell>
      <MediaCell call={call} />
    </TableRow>
  );
}

/**
 * One of my calls in full.
 *
 * Deliberately shorter than the report's dialog next door: the channel uuid, the SIP realm, the
 * codecs and the raw switch event are what somebody debugging the platform needs, and none of
 * them help the person who was on the call. What is here is what they would tell a colleague
 * about it.
 */
/** The chip in the table and on the details; "Handoff" in the room a row has. */
function CallTypeCell({ type }: { type: Cdr["call_type"] }) {
  if (!type) return <span className="text-text-subtle">—</span>;
  return <CallTypeChip type={type} size="sm" short={type === "handoff"} />;
}

function MyCallDialog({
  id, mine, onClose,
}: {
  id: number | undefined;
  mine: readonly string[];
  onClose: () => void;
}) {
  const { user } = useAuth();
  const { data, isPending, error } = useCall(id);
  const other = data ? counterparty(data, mine) : null;
  const placed = data ? myDirection(data, mine) : null;

  return (
    <Dialog open={id !== undefined} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="md" variant="drawer">
        <DialogHeader>
          <DialogTitle>Call details</DialogTitle>
          <DialogDescription>
            Your side of this call, as the switch recorded it.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {isPending ? (
            <div className="flex justify-center py-10">
              <Spinner className="size-5" />
            </div>
          ) : error ? (
            <ErrorState error={error} title="Could not load this call" />
          ) : data && other ? (
            <dl className="divide-y divide-border">
              {/* Callable here too. A rep who opened a call to check how it went is one step
                  from wanting it again, and closing the drawer to find the row they came from
                  would be the only way to get it. */}
              <DescriptionItem label="With">
                <DialNumberButton number={other.number} name={other.name} />
                {other.name ? ` · ${other.name}` : ""}
              </DescriptionItem>
              {/* Not the switch's `direction`, which describes the channel rather than the
                  call: the console places a call by ringing the rep first, so half of what a
                  rep dials is stamped `outbound` on the way out. */}
              <DescriptionItem label="Placed or received">
                {placed === "placed"
                  ? placedBy(data, mine) === "dialer"
                    ? "Placed — the dialer called them for you"
                    : data.kind === "outbound"
                      ? atLeast(user?.role, "supervisor")
                        ? "Placed — you called them through the campaign"
                        : "Placed — you called them"
                      : "Placed — you called them"
                  : placed === "received"
                    ? "Received — your phone was rung"
                    : "—"}
              </DescriptionItem>
              <DescriptionItem label="Started">{formatDateTime(data.start_time)}</DescriptionItem>
              <DescriptionItem label="Answered">
                {data.answer_time ? formatDateTime(data.answer_time) : "Never answered"}
              </DescriptionItem>
              <DescriptionItem label="Ended">{formatDateTime(data.end_time)}</DescriptionItem>
              <DescriptionItem label="Ringing and talking">
                {formatDuration(data.duration_seconds)} total ·{" "}
                {talkShown(data) ? `${formatDuration(data.billsec_seconds)} talk` : "no talk"}
              </DescriptionItem>
              <DescriptionItem label="Outcome">
                <span className="inline-flex items-center gap-1.5">
                  <Badge tone={callOutcome(data).tone} size="sm">
                    {callOutcome(data).label}
                  </Badge>
                  <CallMarkBadge call={data} />
                </span>
              </DescriptionItem>
              {data.audio_in_mos !== null && (
                <DescriptionItem label="How it sounded">
                  <CallQualityDetail call={data} />
                </DescriptionItem>
              )}
              {/* Which side put the phone down. The outcome above does not say — a call both
                  parties were happy with and one the lead cut off are both NORMAL_CLEARING. */}
              {endedBy(data) && <DescriptionItem label="Ended by">{endedBy(data)!.label}</DescriptionItem>}
              <DescriptionItem label="Call type">
                <CallTypeCell type={data.call_type} />
              </DescriptionItem>
              {atLeast(user?.role, "supervisor") && (
                <DescriptionItem label="Campaign">
                  {data.campaign_name ?? (data.campaign_id ? "Deleted campaign" : "—")}
                </DescriptionItem>
              )}
              {/* The person, when the switch knew one: the dialer stamps the lead on both legs, the
                  ACD's lookup on the caller's. The record has the notes and the outcome (ADR-044). */}
              {data.lead_id && (
                <DescriptionItem label="Contact">
                  <Link to={`/contacts/${data.lead_id}`} className="text-accent-11 hover:underline">
                    Open the contact
                  </Link>
                </DescriptionItem>
              )}
              <DescriptionItem label="Leg">
                {CALL_LEG_LABELS[data.leg as keyof typeof CALL_LEG_LABELS] ?? data.leg ?? "—"}
              </DescriptionItem>
              {broughtInBy(data) && (
                <DescriptionItem label="Brought in">{broughtInBy(data)}</DescriptionItem>
              )}
              <DescriptionItem label="Holds and transfers">
                {data.hold_count} hold{data.hold_count === 1 ? "" : "s"}
                {data.hold_seconds > 0 ? ` (${formatDuration(data.hold_seconds)})` : ""} ·{" "}
                {data.transfer_count} transfer{data.transfer_count === 1 ? "" : "s"}
              </DescriptionItem>
              {/* Last, because it is the one row that loads something rather than stating a
                  fact — and because a recording is what you reach for after reading the rest.
                  The transcript rides with it rather than getting a row of its own: they are
                  two views of the same thing, and the motion this exists for is read down,
                  spot something, hear it. */}
              <DescriptionItem label="Recording">
                <CallAudio callId={data.channel_uuid} hasTranscript={data.has_transcript} />
              </DescriptionItem>
            </dl>
          ) : null}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
